import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid';
import copy from 'copy-to-clipboard';
import Markdown from 'markdown-to-jsx';
import { useEffect, useState } from 'react';
import CopySVG from '../../assets/images/icons/CopySVG';
import SendSVG from '../../assets/images/icons/SendSVG';
import useCommonApiCall from '../../hooks/useCommonApiCall';

interface CommonTabContentProps {
	defaultPrompt?: string;
}

const CommonTabContent: React.FC<CommonTabContentProps> = ({ defaultPrompt = '' }) => {
	const [promptValue, setPromptValue] = useState(defaultPrompt);
	const [messageValue, setMessageValue] = useState('');
	const [isPromptCollapsed, setIsPromptCollapsed] = useState(false);
	const [copyButtonText, setCopyButtonText] = useState('Copy');
	const { loading, responseData, callApi } = useCommonApiCall();

	useEffect(() => {
		setPromptValue(defaultPrompt);
	}, [defaultPrompt]);

	// copy response on click
	const copyToClipboard = () => {
		if (responseData) {
			const responseDataString = responseData as string;
			const plainText = responseDataString.replace(/(?:__|[*#])|\[(.*?)\]\(.*?\)/g, '$1');
			copy(plainText);
			setCopyButtonText('Copied');
			setTimeout(() => {
				setCopyButtonText('Copy');
			}, 1000);
		}
	};

	return (
		<div>
			<div className="mb-4 border rounded-md border-gray-300 bg-white">
				<div className="cursor-pointer px-3 py-2 sm:px-3" onClick={() => setIsPromptCollapsed(!isPromptCollapsed)}>
					<h4 className="flex items-center gap-2 text-sm font-normal leading-6 text-gray-900">
						Customise prompt
						{isPromptCollapsed ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
					</h4>
				</div>

				{isPromptCollapsed && (
					<div className="p-4 border-t">
						<textarea
							rows={4}
							className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
							value={promptValue}
							onChange={(e) => setPromptValue(e.target.value)}
							placeholder="Prompt..."
						/>
					</div>
				)}
			</div>
			{/* context info */}
			<h2 className="text-base font-semibold leading-7 text-gray-900 mb-4">Context Information</h2>

			<div className="mb-3">
				<textarea
					rows={5}
					name="comment"
					id="comment"
					className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
					value={messageValue}
					onChange={(e) => setMessageValue(e.target.value)}
				/>
			</div>

			<button
				onClick={() => callApi(promptValue, messageValue)}
				type="button"
				className="inline-flex justify-center rounded-md bg-[#f8c246] px-4 py-2.5 text-sm font-semibold text-black shadow-sm hover:bg-[#f8c246] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
			>
				{loading ? (
					<span role="status">Loading...</span>
				) : (
					<span className="flex gap-2 items-center">
						Submit
						<SendSVG />
					</span>
				)}
			</button>

			{responseData && (
				<div className="mt-4">
					<div className="flex justify-between mb-4 items-center">
						<h3 className="font-medium">Api Response:</h3>
						<button
							onClick={copyToClipboard}
							type="button"
							className="border px-2 py-1 text-sm font-semibold rounded flex gap-1 items-center"
						>
							<CopySVG />
							{copyButtonText}
						</button>
					</div>
					<div className="responseWrapper p-4 border rounded-md border-gray-300">
						<Markdown>{responseData}</Markdown>
					</div>
				</div>
			)}
		</div>
	);
};

export default CommonTabContent;
